import React, { FC, memo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CustomInputWithLabel from '../../common/CustomInputWithLabel';
import LabelWrapper from '../../common/LabelWrapper';
import CustomInput from '../../custom/CustomInput';
import BoxComponent from '../../custom/BoxComponent';
import CustomCheckbox from '../../custom/CustomCheckbox';
import { SdtPlotArea } from '../../../models/inputTypes/SdtFields';

const styles = {
  spacingContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  alignEndContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  largeChildSpacing: {
    '& >:nth-child(n)': {
      my: 1,
      mr: { xs: 4, md: 6, lg: 8 },
    },
  },
  smallChildSpacing: {
    '& >:nth-child(n)': {
      my: 3,
      mr: 4,
    },
  },

  smallVariableSpacing: {
    '& >:nth-child(n)': {
      my: 1,
      mr: 4,
    },
    '& >:first-child': {
      my: 1,
      mr: 2,
    },
  },
  coordinates: {
    '& >:nth-child(2)': {
      ml: 1,
    },
  },
} as const;

type SdtPlotAreasInputsGroupProps = {
  removeTimeParams?: boolean;
  inputState: SdtPlotArea;
  setInputState: (value: SdtPlotArea) => void;
};

const SdtPlotAreasInputsGroup: FC<SdtPlotAreasInputsGroupProps> = ({
  removeTimeParams = false,
  inputState,
  setInputState,
}) => {
  const [valueX, setValueX] = useState(1);
  const [valueY, setValueY] = useState(1);
  const onCheckboxClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    const currentValue: 'bl' | 'dhdt' | 'qtx' | 'qty' | 'qtmag' | 'qtdir' | 'init_depths' = e.target.id;
    const newValue = inputState[currentValue] ? 0 : 1;
    setInputState({ ...inputState, [currentValue]: newValue });
  };

  const onInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };

  const onNumberChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };

  const onInputChangeX = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setValueX(+event.target.value);
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };
  const onInputChangeY = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setValueY(+event.target.value);
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };

  return (
    <Box>
      <Typography mb={1} variant={'subtitle1'}>
        Bounding Points & Grid Cell Node Numbers
      </Typography>
      <Box mb={2} sx={{ ...styles.spacingContainer, ...styles.largeChildSpacing }}>
        <Box>
          <LabelWrapper label={'Southwestern point'}>
            <Box sx={{ display: 'flex', ...styles.coordinates }}>
              <CustomInputWithLabel
                min={1}
                step={1}
                required
                errorText={'Enter positive integer'}
                type={'number'}
                onChange={onNumberChange}
                value={inputState.sw_x}
                name={'sw_x'}
                label={'X'}
              />
              <CustomInputWithLabel
                min={1}
                step={1}
                required
                errorText={'Enter positive integer'}
                type={'number'}
                onChange={onNumberChange}
                value={inputState.sw_y}
                name={'sw_y'}
                label={'Y'}
              />
            </Box>
          </LabelWrapper>
        </Box>
        <Box>
          <LabelWrapper label={'Northeastern point'}>
            <Box sx={{ display: 'flex', ...styles.coordinates }}>
              <CustomInputWithLabel
                min={inputState.sw_x}
                step={1}
                required
                errorText={
                  valueX < inputState.sw_x && valueX > 0
                    ? 'Number should be >= than first point'
                    : 'Enter positive integer'
                }
                type={'number'}
                onChange={onInputChangeX}
                value={inputState.ne_x}
                name={'ne_x'}
                label={'X'}
              />
              <CustomInputWithLabel
                min={inputState.sw_y}
                step={1}
                required
                errorText={
                  valueY < inputState.sw_y && valueY > 0
                    ? 'Number should be >= than first point'
                    : 'Enter positive integer'
                }
                type={'number'}
                onChange={onInputChangeY}
                value={inputState.ne_y}
                name={'ne_y'}
                label={'Y'}
              />
            </Box>
          </LabelWrapper>
        </Box>
      </Box>
      <Typography mx={1} variant={'subtitle1'}>
        Variables
      </Typography>
      <Box sx={{ ...styles.smallChildSpacing }}>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.bl} id={'bl'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.bl}>
            <LabelWrapper label={'Bed Level (m)'}>
              <CustomInput
                disabled={!inputState.bl}
                required
                minWidth={'160px'}
                errorText={'Please enter file name'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_bl}
                name={'name_bl'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.dhdt} id={'dhdt'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.dhdt}>
            <LabelWrapper label={'Rate of Bed Level Changes (m/day)'}>
              <CustomInput
                disabled={!inputState.dhdt}
                required
                minWidth={'160px'}
                errorText={'Please enter file name'}
                type={'text'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_dhdt}
                name={'name_dhdt'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.qtx} id={'qtx'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.qtx}>
            <LabelWrapper label={'Total Load Transport x Component (m\u00B3/s/m)'}>
              <CustomInput
                disabled={!inputState.qtx}
                required
                minWidth={'160px'}
                errorText={'Please enter file name'}
                type={'text'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_qtx}
                name={'name_qtx'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.qty} id={'qty'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.qty}>
            <LabelWrapper label={'Total Load Transport y Component (m\u00B3/s/m)'}>
              <CustomInput
                disabled={!inputState.qty}
                required
                minWidth={'160px'}
                errorText={'Please enter file name'}
                type={'text'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_qty}
                name={'name_qty'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer, ...styles.smallVariableSpacing }}>
          <Box mr={2}>
            <CustomCheckbox checked={!!inputState.qtmag} id={'qtmag'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.qtmag}>
            <LabelWrapper label={'Total Load Transport Magnitude (m\u00B3/s/m)'}>
              <CustomInput
                disabled={!inputState.qtmag}
                required
                type={'text'}
                minWidth={'160px'}
                errorText={'Please enter file name'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_qtmag}
                name={'name_qtmag'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mb={1} mr={2}>
            <CustomCheckbox checked={!!inputState.qtdir} id={'qtdir'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.qtdir}>
            <LabelWrapper label={'Total Load Transport Direction (deg)'}>
              <CustomInput
                required
                disabled={!inputState.qtdir}
                type={'text'}
                minWidth={'160px'}
                errorText={'Please enter file name'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_qtdir}
                name={'name_qtdir'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
        <Box sx={{ ...styles.alignEndContainer }}>
          <Box mb={1} mr={2}>
            <CustomCheckbox checked={!!inputState.init_depths} id={'init_depths'} onClick={onCheckboxClick} />
          </Box>
          <BoxComponent disabled={!inputState.init_depths}>
            <LabelWrapper label={'Initial Depths (m)'}>
              <CustomInput
                required
                disabled={!inputState.init_depths}
                type={'text'}
                minWidth={'160px'}
                errorText={'Please enter file name'}
                maxLength={30}
                onChange={onInputChange}
                value={inputState.name_init_depths}
                name={'name_init_depths'}
              />
            </LabelWrapper>
          </BoxComponent>
        </Box>
      </Box>
      {!removeTimeParams && (
        <>
          <Box mt={2} mb={1}>
            <Typography variant={'subtitle1'}>Time Parameters</Typography>
          </Box>
          <Box sx={{ ...styles.spacingContainer, ...styles.smallChildSpacing }}>
            <Box>
              <LabelWrapper label={'Start time step'}>
                <CustomInput
                  min={1}
                  step={1}
                  type={'number'}
                  errorText={'Enter positive integer'}
                  required
                  onChange={onNumberChange}
                  value={inputState.start_step}
                  name={'start_step'}
                />
              </LabelWrapper>
            </Box>
            <Box>
              <LabelWrapper label={'End time step'}>
                <CustomInput
                  min={1}
                  step={1}
                  type={'number'}
                  errorText={'Enter positive integer'}
                  required
                  onChange={onNumberChange}
                  value={inputState.end_step}
                  name={'end_step'}
                />
              </LabelWrapper>
            </Box>
            <Box>
              <LabelWrapper label={'Time step interval'}>
                <CustomInput
                  min={1}
                  step={1}
                  required
                  errorText={'Enter positive integer'}
                  type={'number'}
                  onChange={onNumberChange}
                  value={inputState.interval}
                  name={'interval'}
                />
              </LabelWrapper>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default memo(SdtPlotAreasInputsGroup);
